import {inject, Injectable} from '@angular/core';
import { AppConsts } from '../../shared/AppConsts';
import { AppSessionService } from '../../shared/common/session/app-session.service';
import {ENVIRONMENT} from "@shared/utils/environment";

const defaultCache = false;
const defaultCircuitBreakerTimeout = 3;
@Injectable({
    providedIn: 'root',
})
export class FeatureFlagService {
    private client: any;
    private readonly environment = inject(ENVIRONMENT);

    constructor(private readonly appSessionService: AppSessionService) {
        const { environmentMappingFeatureFlag } = AppConsts;
        const config = {
            mock: '',
            endpoint: this.environment.isLocal() ? environmentMappingFeatureFlag.local : environmentMappingFeatureFlag.production,
            defaultCache,
            defaultCircuitBreakerTimeout,
        };

        this.client = (window as any).appconfigr.client(config);
    }

    /**
     * Returns True if the Feature Flag is enabled for the given context
     * @param flagKey Flag Name
     * @param context Flag Context
     * @param defaultValue Default value in case the flag doesn't exist
     * @returns Promise<boolean>
     */
    isFlagEnabled(flagKey: string, context: any = {}, defaultValue: boolean = false): Promise<boolean> {
        return this.client.isFlagEnabled(
            {
                entityContext: {
                    ...context,
                    namespace: this.environment.namespace,
                    currentuser_id: this.appSessionService.userId,
                    currentuser_email: this.appSessionService.user?.emailAddress,
                    currentuser_roles: this.appSessionService.user?.details.map((p) => p.name),
                    currentuser_advisor_id: this.appSessionService.user?.partnerId,
                    currentuser_company_uuid: this.appSessionService.user?.companyUuid,
                },
                flagKey: flagKey,
            },
            defaultValue
        );
    }

    /**
     * Returns a collection of flags via a batch request
     * @param flagKeys Array of Flag Keys
     * @param entities Entity contexts
     * @returns Collection of feature flags
     */
    areFlagsEnabled(flagKeys: string[], entities: any[]): Promise<any> {
        return this.client.batchFlagsEnabled({
            entities: entities.map((p) => {
                if (p.entityContext) {
                    p.entityContext.namespace = this.environment.namespace;
                    p.entityContext.currentuser_id = this.appSessionService.userId;
                    p.entityContext.currentuser_advisor_id = this.appSessionService.user?.partnerId;
                    p.entityContext.currentuser_email = this.appSessionService.user?.emailAddress;
                    p.entityContext.currentuser_roles = this.appSessionService.user?.details.map((p) => p.name);
                    p.entityContext.currentuser_company_uuid = this.appSessionService.user?.companyUuid;
                } else {
                    p.entityContext = {
                        namespace: this.environment.namespace,
                        currentuser_id: this.appSessionService.userId,
                        currentuser_advisor_id: this.appSessionService.user?.partnerId,
                        currentuser_email: this.appSessionService.user?.emailAddress,
                        currentuser_roles: this.appSessionService.user?.details.map((p) => p.name),
                        currentuser_company_uuid: this.appSessionService.user?.companyUuid,
                    };
                }
                return p;
            }),
            flagKeys: flagKeys,
        });
    }

    /**
     * Loads the passed feature flags
     * @param flagKeys Feature Flag Keys
     * @param entities Context
     * @returns Object with the Feature Flags and its value
     */
    async loadFeatureFlags(flagKeys: string[], entities: any[]): Promise<{}> {
        const flags = await this.areFlagsEnabled(flagKeys, entities);
        const result = {};

        flags.forEach((flag) => {
            result[flag.flagKey] = flag.variant;
        });

        return result;
    }
}
